import { useMemo } from 'react';
import { Select, type SelectProps } from '@mantine/core';
import { useCostCentres } from '../api';

interface CostCentreSelectProps extends Omit<SelectProps, 'data' | 'value' | 'onChange' | 'searchable'> {
  siteId: string | undefined;
  value: string;
  onChange: (costCentreId: string, label: string) => void;
}

/** Cost centre picker, scoped to the chosen site (Dryer, Hammer Mills, PM1-3, Mobile
 * Plant, Site General… — BUILD_SPEC.md §5.1). Disabled until a site is chosen. */
export function CostCentreSelect({ siteId, value, onChange, ...rest }: CostCentreSelectProps) {
  const { data, isLoading } = useCostCentres(siteId);
  const options = useMemo(() => (data?.items ?? []).map((cc) => ({ value: cc.id, label: `${cc.code} — ${cc.name}` })), [data]);

  return (
    <Select
      {...rest}
      data={options}
      value={value || null}
      disabled={!siteId || rest.disabled}
      placeholder={!siteId ? 'Choose a site first' : (rest.placeholder ?? 'Choose a cost centre…')}
      description={rest.description ?? 'Which department or process this cost gets attributed to'}
      onChange={(newValue) => {
        const match = newValue ? options.find((o) => o.value === newValue) : undefined;
        onChange(newValue ?? '', match?.label ?? '');
      }}
      nothingFoundMessage={isLoading ? 'Loading…' : 'No cost centres found'}
      comboboxProps={{ withinPortal: true }}
    />
  );
}
